//@flow
import * as React from 'react';
import classnames from 'classnames';
import { themr } from 'react-css-themr';
import btnTheme from './Button.scss';
import Button from 'react-toolbox/lib/button';
import MlFontIcon from 'common/components/core/font_icon/MlFontIcon';
import includes from 'lodash/includes';
import BorderColor from 'common/components/core/colors/border.scss';
import { Loader } from 'common/components/core/loader/Loader';
import { SizeEnum } from 'common/enums/UIEnums';

type Props = {
    label: string | any,
    theme: Object,
    buttonType?: string,
    icon?: ?string | any,
    disabled?: boolean,
    disableClick?: boolean,
    disableTab?: boolean,
    tabIndex?: string,
    className?: string,
    color?: string,
    underline?: boolean,
    uppercase?: boolean,
    semibold?: boolean,
    neutral?: boolean,
    enableOnHover?: boolean,
    enableTooltipOnHover?: boolean,
    borderColor?: string,
    onClick: () => void,
    circle?: boolean,
    inverted?: boolean,
    smallLoader?: boolean,
    isLoading?: boolean,
    isPopOverOpen?: boolean,
    countLabelButton?: boolean,
    fieldId?: string,
    testId?: string
};

export type IconSourceEnum = IconSources.Dashboard | IconSources.Neo;

export const ButtonTypes = {
    Solid: 'SOLID',
    Text: 'TEXT',
    Fab: 'FAB',
    Link: 'LINK',
    Flat: 'FLAT',
    Icon: 'ICON',
    Outline: 'OUTLINE'
};
const defaults = {
    theme: btnTheme,
    ButtonComponent: Button
};

const customButtonFactory = ({
    Button = defaults.ButtonComponent,
    options = { theme: defaults.theme }
}) => {
    class CustomButton extends React.PureComponent<Props> {
        static defaultProps = {
            icon: null,
            disableClick: true
        };
        handleClick = (...args) => {
            const { onClick, disabled, fieldId } = this.props;
            if (disabled) {
                return;
            }
            if (fieldId) {
                onClick(fieldId, ...args);
            } else {
                onClick(...args);
            }
        };
        render() {
            const {
                theme,
                buttonType,
                icon,
                disabled,
                disableClick,
                disableTab,
                tabIndex,
                className,
                color,
                uppercase,
                underline,
                semibold,
                neutral,
                enableOnHover,
                enableTooltipOnHover,
                borderColor,
                onClick,
                circle,
                inverted,
                isLoading,
                label,
                isPopOverOpen,
                countLabelButton,
                smallLoader,
                isButtonSelected,
                size,
                tooltipHideDelay, //eslint-disable-line no-unused-vars
                testId,
                ...others
            } = this.props;
            const sizeClass = SizeEnum.getSizeClass({ theme, size });
            const buttonTypeClass = includes(ButtonTypes, buttonType)
                ? theme[`${buttonType.toLowerCase()}Btn`]
                : null;
            let buttonIcon = null;
            if (icon) {
                if (typeof icon === 'string') {
                    buttonIcon = (
                        <MlFontIcon
                            iconClass={isNaN(icon) ? icon : null}
                            numberLabel={!isNaN(icon) ? icon : null}
                            className={theme.buttonIcon}
                        />
                    );
                } else if (typeof icon === 'object') {
                    buttonIcon = icon;
                }
            }

            const loadingIcon = <Loader active={isLoading} theme={theme} />;
            const buttonLabel = label ? (
                <span className={theme.buttonLabel}>{label}</span>
            ) : null;
            return (
                <Button
                    {...others}
                    label={buttonLabel}
                    theme={theme}
                    icon={isLoading ? loadingIcon : buttonIcon}
                    ripple={!disabled}
                    onClick={
                        disableClick && disabled && !enableOnHover
                            ? null
                            : onClick && this.handleClick
                    }
                    tabIndex={
                        tabIndex
                            ? tabIndex
                            : disableTab && disabled
                            ? '-1'
                            : null
                    }
                    neutral={neutral ? neutral : false}
                    testId={testId}
                    className={classnames({
                        [theme.countLabelButton]: countLabelButton,
                        [theme.isButtonPopoverOpen]:
                            isPopOverOpen && theme.isButtonPopoverOpen,
                        [theme.inverted]: inverted,
                        [theme.customButton]: theme.customButton,
                        [theme.disabled]: disabled,
                        [theme.uppercase]: uppercase,
                        [theme.underline]: underline,
                        [theme.circle]: circle,
                        [buttonTypeClass]: buttonTypeClass,
                        [className]: className,
                        [theme[color]]: color,
                        [theme.semibold]: semibold,
                        [theme.enableOnHover]: enableOnHover,
                        [theme.enableTooltipOnHover]: enableTooltipOnHover,
                        [theme.isLoading]: isLoading,
                        [theme.smallLoader]: smallLoader,
                        [BorderColor[borderColor]]: borderColor,
                        [theme.isButtonSelected]: isButtonSelected,
                        [sizeClass]: !!sizeClass
                    })}
                />
            );
        }
    }

    return themr('CustomButton', options.theme)(CustomButton);
};

const ButtonComponent = customButtonFactory({});
export default ButtonComponent;
export { ButtonComponent as Button, customButtonFactory };
